import { useCallback } from 'react';
import { isTauri } from '../utils/tauri';
import {
  isGitWorkspace,
  isLocalWorkspace,
  isBackendWorkspace,
  buildLocalManifestEntries,
} from '../utils/workspaceStorage';
import {
  openGitWorkspaceRoot,
  exportWorkspaceToDirectory,
  loadWorkspaceSnapshot,
} from '../services/workspaceStoreRouter';
import { saveLocalWorkspaceManifest, snapshotDirAbsolute } from '../services/localWorkspaceStore';
import { collectionService } from '../services/collectionService';
import { toCloudImportCollection, planCloudImportBatches } from '../utils/cloudImportPayload';
import { saveImportedEnvironmentsToCloud } from '../services/cloudEnvironmentImport';
import { mapApiRequestToSidebar, mapImportedRequestToLocal } from '../utils/requestMappers';
import { markLocalOnly } from '../services/localCollectionLink';
import { useAuthStore } from '../store/authStore';
import { useWorkspaceStore, setWorkspaces, setCurrentWorkspace } from '../store/workspaceStore';
import { setCollections } from '../store/collectionsStore';
import { requireWritableWorkspace } from '../plans/localGate';
import { requireApiCapacity, requireApiRoom } from '../plans/localApiUsage';
import { countApiItems } from '../plans/apiCounts';
import { requireWorkspaceSlot } from '../plans/localWorkspaces';
import { useUiModalStore, setImportCollectionLoading, setShowImportCollectionModal } from '../store/uiModalStore';
import { parseImportFile } from '../utils/collectionImport';
import { isAcceptedFile, isHarFile } from '../utils/collectionImport/acceptedFiles';
import { useVariablesStore, setEnvironments, setSelectedEnvironment } from '../store/variablesStore';
import { importFailureToast } from '../utils/importFailureToast';

// Same id shape aiCreateEnvironment uses (useAiWorkspaceActions.js).
const genEnvId = () => `env_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

/** Same FileReader channel the Import dialog's processFile uses. */
const readFileAsText = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error('read failed'));
    reader.readAsText(file);
  });

/**
 * Reveal-in-explorer, open/export git+local workspaces, and collection import
 * (Bruno folder + normalized collections). Tauri-only paths early-return outside
 * the desktop app. Cross-domain reads via store getState().
 *
 * @param {{ toastT: Function, snapshotHydratedRef: { current: any } }} deps
 */
export function useImportExport({ toastT, snapshotHydratedRef }) {
  /**
   * The folder is resolved HERE, not in Rust. A git workspace already carries
   * its own `rootPath`; a local workspace lives at
   * `<AppLocalData>/local-workspaces/ns-<namespace>/<id>`, and the namespace
   * segment is knowledge this side owns — Rust used to rebuild the path without
   * it (and inside the account root, which local workspaces deliberately sit
   * outside of), so it never resolved to anything that exists.
   *
   * The failure is also reported now. This handler is only wired up for local
   * workspaces (see App.js), so swallowing the error into console.error made
   * the menu item a silent no-op for every single user who clicked it.
   */
  const handleRevealInExplorer = useCallback(async () => {
    const currentWorkspace = useWorkspaceStore.getState().currentWorkspace;
    if (!isTauri() || !currentWorkspace?.id) return;
    try {
      const { invoke } = await import('@tauri-apps/api/core');
      const rootPath = isGitWorkspace(currentWorkspace)
        ? currentWorkspace.rootPath
        : await snapshotDirAbsolute(currentWorkspace.id);
      await invoke('reveal_in_explorer', { localId: currentWorkspace.id, rootPath });
    } catch (err) {
      console.error('[RevealInExplorer] Failed to open folder:', err);
      toastT('toast.revealInExplorerFailed', 'error', {
        message: err?.message || String(err ?? ''),
      });
    }
  }, [toastT]);

  const handleOpenGitWorkspace = useCallback(async () => {
    if (!isTauri()) {
      toastT('toast.gitWorkspaceDesktopOnly', 'warning');
      return;
    }
    try {
      const { open } = await import('@tauri-apps/plugin-dialog');
      const selected = await open({ directory: true, multiple: false, title: 'Open Git Workspace' });
      if (!selected) return;
      const rootPath = Array.isArray(selected) ? selected[0] : selected;
      // Plans v2: an opened git folder is another local workspace (re-opening
      // one already in the list is not).
      const known = useWorkspaceStore.getState().workspaces.some((w) => w.rootPath === rootPath);
      if (!known && !requireWorkspaceSlot()) return;
      const ws = await openGitWorkspaceRoot(rootPath);
      setWorkspaces((prev) => {
        const next = [...prev.filter((w) => String(w.id) !== String(ws.id)), ws];
        saveLocalWorkspaceManifest(buildLocalManifestEntries(next)).catch(() => {});
        return next;
      });
      setCurrentWorkspace(ws);
      snapshotHydratedRef.current = null;
      toastT('toast.gitWorkspaceOpened', 'success', { name: ws.name });
    } catch (err) {
      console.error('[OpenGitWorkspace] Failed:', err);
      toastT('toast.gitWorkspaceOpenFailed', 'error', { message: err.message || '' });
    }
  }, [toastT, snapshotHydratedRef]);

  const handleExportWorkspaceToFolder = useCallback(async () => {
    const currentWorkspace = useWorkspaceStore.getState().currentWorkspace;
    const isAuthenticated = useAuthStore.getState().isAuthenticated;
    if (!currentWorkspace || !isLocalWorkspace(currentWorkspace, isAuthenticated)) return;
    if (!isTauri()) {
      toastT('toast.gitWorkspaceDesktopOnly', 'warning');
      return;
    }
    try {
      const { open } = await import('@tauri-apps/plugin-dialog');
      const selected = await open({ directory: true, multiple: false, title: 'Export workspace to folder' });
      if (!selected) return;
      const rootPath = Array.isArray(selected) ? selected[0] : selected;
      const snap = await loadWorkspaceSnapshot(currentWorkspace);
      await exportWorkspaceToDirectory(rootPath, snap, {
        id: currentWorkspace.id,
        name: currentWorkspace.name,
      });
      toastT('toast.workspaceExported', 'success', { name: currentWorkspace.name });
    } catch (err) {
      console.error('[ExportWorkspace] Failed:', err);
      toastT('toast.workspaceExportFailed', 'error', { message: err.message || '' });
    }
  }, [toastT]);

  const handleImportCollections = useCallback(async (normalizedCollections, { silent = false } = {}) => {
    if (!normalizedCollections?.length) return null;
    const currentWorkspace = useWorkspaceStore.getState().currentWorkspace;
    const isAuthenticated = useAuthStore.getState().isAuthenticated;

    if (isAuthenticated && !currentWorkspace) {
      toastT('toast.noWorkspaceForCollection', 'warning');
      return null;
    }
    // Plans v2: an own cloud workspace is read-only without cloud_sync.
    if (!requireWritableWorkspace(currentWorkspace)) return null;

    setImportCollectionLoading(true);
    let importedCollections = 0;
    let importedRequests = 0;

    try {
      if (isBackendWorkspace(currentWorkspace, isAuthenticated) && currentWorkspace) {
        // ONE call. This used to be a POST per collection plus a POST per
        // request, awaited in sequence — 327 round-trips for a real Postman
        // export, roughly a minute behind a spinner with no progress and no
        // cancel, which users reported as the app freezing. Only cloud
        // workspaces were affected; the local branch below is an in-memory map
        // and always returned instantly, which is what made the report so
        // confusing to read.
        //
        // The server wraps it in a transaction, so the manual "delete the
        // collection we just made" rollback that used to live here is gone. It
        // could not be atomic over HTTP anyway: if that delete failed, or the app
        // was closed mid-import, the user was left with a partial collection and
        // no way to tell which requests were missing.
        //
        // Split ONLY by the server's body cap (cloudImportPayload.js): a real
        // import — even a 184-operation Swagger file — is one call.
        const created = [];
        const batches = planCloudImportBatches(normalizedCollections.map(toCloudImportCollection));
        let batchError = null;
        for (const batch of batches) {
          try {
            created.push(...(await collectionService.importCollections(currentWorkspace.id, batch)));
          } catch (err) {
            // Nothing committed yet: the plain failure path below (dialog stays
            // open, retry is safe). Otherwise the earlier batches ARE on the
            // server — show them, say "partial", and close the dialog, so a
            // one-click retry cannot import them a second time.
            if (created.length === 0) throw err;
            batchError = err;
            break;
          }
        }

        const createdCollections = created.map((col) => ({
          ...col,
          requests: (col.requests || []).map(mapApiRequestToSidebar),
        }));

        importedCollections = createdCollections.length;
        importedRequests = createdCollections.reduce((n, c) => n + c.requests.length, 0);

        setCollections((prev) => [...prev, ...createdCollections]);
        if (batchError) {
          console.error('Import partially failed:', batchError);
          toastT('toast.importPartiallyFailed', 'error', {
            collections: importedCollections,
            total: normalizedCollections.length,
            message: batchError.message || '',
          });
          setShowImportCollectionModal(false);
          return { collections: importedCollections, requests: importedRequests, partial: true };
        }
        if (!silent) {
          toastT('toast.collectionImported', 'success', {
            collections: importedCollections,
            requests: importedRequests,
          });
        }
      } else {
        // Plans v2: an import adds its collections and all their requests.
        if (!(await requireApiRoom(countApiItems(normalizedCollections)))) return null;
        const localCols = normalizedCollections.map((col, cIdx) => {
          const id = `${Date.now()}-${cIdx}`;
          const requests = (col.requests || []).map((r, i) => mapImportedRequestToLocal(r, id, i));
          importedRequests += requests.length;
          return markLocalOnly({
            id,
            name: col.name,
            description: col.description || '',
            settings: col.settings || {},
            requests,
          });
        });
        importedCollections = localCols.length;
        setCollections((prev) => [...prev, ...localCols]);
        if (!silent) {
          toastT('toast.collectionImportedLocal', 'info', {
            collections: importedCollections,
            requests: importedRequests,
          });
        }
      }
      setShowImportCollectionModal(false);
      return { collections: importedCollections, requests: importedRequests };
    } catch (error) {
      console.error('Failed to import collections:', error);

      // A plan limit gets its own message naming the two ways forward, rather
      // than a dead-end "Import failed" — see utils/importFailureToast.js.
      const { key, type, vars } = importFailureToast(error);
      toastT(key, type, vars);
      return null;
    } finally {
      setImportCollectionLoading(false);
    }
  }, [toastT]);


  /**
   * Import a parsed environment (Postman / Hoppscotch file, or one that came
   * with a Bruno collection).
   *
   * Same path aiCreateEnvironment takes: setEnvironments + selection; local
   * workspaces persist via the debounced snapshot writer that watches the
   * store (useWorkspacePersistence). Name collisions get " (2)"-style
   * suffixes instead of an error — an import should land, not bounce.
   * `select: false` (environments that came WITH a collection, O4) adds it
   * without switching the user's active environment.
   *
   * Async (Plans v2: the cap counts every own local workspace): resolves the
   * environment, or null when nothing was added.
   */
  const handleImportEnvironment = useCallback(async (environment, { silent = false, select = true } = {}) => {
    const baseName = String(environment?.name || '').trim();
    if (!baseName) return null;

    if (!requireWritableWorkspace() || !(await requireApiCapacity('environments'))) return null;
    const environments = useVariablesStore.getState().environments || [];
    const taken = new Set(environments.map((e) => String(e.name || '').trim().toLowerCase()));
    let name = baseName;
    for (let n = 2; taken.has(name.toLowerCase()); n++) name = `${baseName} (${n})`;

    const env = { id: genEnvId(), name, variables: environment.variables || [] };
    setEnvironments([...environments, env]);
    if (select) setSelectedEnvironment(env);
    // A cloud workspace keeps environments on the server — without this the
    // import vanished on reload. Several environments in one import share ONE
    // PUT (cloudEnvironmentImport.js); a no-op for local and git workspaces.
    saveImportedEnvironmentsToCloud({
      onError: (err) => toastT('toast.persistEnvironmentFailed', 'error', { message: err?.message || '' }),
    });
    if (!silent) {
      toastT('toast.environmentImported', 'success', { name, count: env.variables.length });
    }
    setShowImportCollectionModal(false);
    return env;
  }, [toastT]);

  /**
   * Direct import for files dropped on the collections sidebar (owner call,
   * 24.08.2026): NO modal. Acceptance, read and parse errors surface as
   * error toasts with the same copy the Import dialog shows; success goes
   * through the SAME import handlers the dialog's confirm uses, then one
   * success toast (with a short HAR note appended for .har files instead
   * of the dialog's blocking warning).
   */
  const handleDroppedImportFile = useCallback(async (file) => {
    if (!file) return;
    // D1: one import at a time — a second drop while a (possibly cloud) import
    // is in flight must not run a parallel import. Same flag the modal's
    // button disables on.
    if (useUiModalStore.getState().importCollectionLoading) {
      toastT('toast.importAlreadyRunning', 'error');
      return;
    }
    if (!isAcceptedFile(file)) {
      toastT('import.unsupportedFileType', 'error');
      return;
    }

    let text;
    try {
      text = await readFileAsText(file);
    } catch {
      toastT('import.readFailed', 'error');
      return;
    }

    let result;
    try {
      result = parseImportFile(text, file.name);
    } catch (err) {
      // parseImportFile throws the user-readable messages the dialog
      // displays ('Unrecognized format…') — but js-yaml errors carry a
      // multi-line source snippet (D3): clamp to the first line, 120 chars.
      const message = String(err.message || '').split('\n')[0].slice(0, 120);
      toastT('toast.importCollectionFailed', 'error', { message });
      return;
    }

    if (result.kind === 'environment') {
      // One file may hold several (a Hoppscotch "export all"): the first is selected as before.
      const envs = result.environments?.length ? result.environments : [result.environment];
      // One after another: each one counts against the plan's cap.
      const added = [];
      for (let i = 0; i < envs.length; i += 1) {
        const env = await handleImportEnvironment(envs[i], { silent: true, select: i === 0 });
        if (env) added.push(env);
      }
      if (added.length) {
        toastT('toast.droppedEnvironmentImported', 'success', {
          name: added.map((e) => e.name).join(', '),
          count: added.reduce((n, e) => n + e.variables.length, 0),
        });
      }
      return;
    }

    const imported = await handleImportCollections(result.collections, { silent: true });
    if (!imported) return; // failures already toasted inside handleImportCollections

    // Environments that came with the collection (Bruno exports): added, never selected (O4).
    for (const env of result.environments || []) {
      await handleImportEnvironment(env, { silent: true, select: false });
    }
    if (imported.partial) return; // the partial-failure toast already said what landed

    if (imported.collections > 1) {
      // Multi-collection files (e.g. Insomnia exports) keep the count-based
      // copy — naming only the first would misdescribe the import. HAR
      // always parses to exactly one collection, so no HAR variant here.
      toastT('toast.collectionImported', 'success', {
        collections: imported.collections,
        requests: imported.requests,
      });
      return;
    }

    toastT(
      isHarFile(file) ? 'toast.droppedCollectionImportedHar' : 'toast.droppedCollectionImported',
      'success',
      { name: result.collections[0]?.name || '', count: imported.requests }
    );
  }, [toastT, handleImportCollections, handleImportEnvironment]);

  return {
    handleRevealInExplorer,
    handleOpenGitWorkspace,
    handleExportWorkspaceToFolder,
    handleImportCollections,
    handleImportEnvironment,
    handleDroppedImportFile,
  };
}
